import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { extractAxiosError } from "../lib/api";
import { mapValidationErrors } from "../lib/formErrors";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authenticated
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // One-time cleanup of any stale Bearer token left over from the previous
    // localStorage-based auth (pre-2026-06 migration). Cookies handle auth now.
    // localStorage can throw in Safari private mode and disabled-storage Firefox
    // configurations — log the warning instead of swallowing silently.
    try {
      localStorage.removeItem("pq_access_token");
    } catch (e) {
      console.warn("[auth] legacy token cleanup skipped (storage unavailable):", e?.message || e);
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Two-factor gate: backend issued a challenge instead of an access token.
      // The page is responsible for prompting the OTP and calling verify2faLogin.
      if (data.pending_2fa) {
        return {
          ok: false,
          code: "TWOFA_REQUIRED",
          challengeToken: data.challenge_token,
          devCode: data.dev_code, // preview env only
        };
      }
      // Auth state lives 100% in httpOnly Secure SameSite=None cookies set by
      // the backend's Set-Cookie response header. We do NOT mirror the token
      // into localStorage (XSS-safe by design).
      // The login response is a partial user (no country/VAT/address):
      // load the full profile so Settings and analytics see real values.
      try {
        const { data: me } = await api.get("/auth/me");
        setUser(me);
      } catch {
        setUser(data);
      }
      return { ok: true };
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "EMAIL_NOT_VERIFIED") {
        setError("La tua email non è ancora stata verificata. Controlla la casella o richiedi un nuovo link.");
        return { ok: false, code: "EMAIL_NOT_VERIFIED" };
      }
      const msg = extractAxiosError(e, "Errore di accesso");
      console.error("Login failed:", msg, e);
      setError(msg);
      return { ok: false };
    }
  }, []);

  const verify2faLogin = useCallback(async (challengeToken, code) => {
    setError("");
    try {
      const { data } = await api.post("/auth/2fa/login-verify", {
        challenge_token: challengeToken, code,
      });
      // 2FA success → backend has already set httpOnly cookies on the response.
      try {
        const { data: me } = await api.get("/auth/me");
        setUser(me);
      } catch {
        setUser(data);
      }
      return { ok: true };
    } catch (e) {
      const msg = extractAxiosError(e, "Codice non valido");
      setError(msg);
      return { ok: false };
    }
  }, []);

  const register = useCallback(async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      // The backend signs the user in on registration (httpOnly cookies are
      // already set on this response), so the app can go straight to the
      // dashboard. A reminder banner handles the pending email verification.
      setUser(data);
      return { ok: true, data };
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      // Field-level errors are localised by the form, never shown raw.
      return {
        ok: false,
        status,
        code: typeof detail === "string" ? detail : "",
        validation: status === 422 ? mapValidationErrors(detail) : {},
        network: !e.response,
      };
    }
  }, []);

  const logout = useCallback(async () => {
    // Best-effort backend logout: even if it fails (offline, expired token),
    // we still clear client state below. Backend's clear_auth_cookies sends
    // matching SameSite=None Secure Set-Cookie max_age=0 headers so the
    // browser drops the httpOnly cookies. There is no localStorage to clean
    // — auth is 100% cookie-based.
    try { await api.post("/auth/logout"); } catch (e) { console.warn("logout backend call failed (continuing client logout):", e?.message); }
    setUser(false);
  }, []);

  const ctxValue = useMemo(
    () => ({ user, error, login, register, logout, refresh, verify2faLogin }),
    [user, error, login, register, logout, refresh, verify2faLogin]
  );

  return (
    <AuthContext.Provider value={ctxValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
